import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/apiService';

export interface Address {
  id: string;
  name: string;
  phone: string;
  email: string;
  houseFlatNo: string;
  address: string;
  city: string;
  pincode: string;
  state: string;
  type: 'home' | 'office' | 'other';
  isDefault: boolean;
}

interface AddressContextType {
  addresses: Address[];
  addAddress: (address: Omit<Address, 'id'>) => Promise<void>;
  updateAddress: (id: string, address: Omit<Address, 'id'>) => Promise<void>;
  deleteAddress: (id: string) => Promise<void>;
  setDefaultAddress: (id: string) => void;
  getDefaultAddress: () => Address | undefined;
  loadAddresses: () => Promise<void>;
}

const AddressContext = createContext<AddressContextType | undefined>(undefined);

export const AddressProvider = ({ children }: { children: React.ReactNode }) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const { isAuthenticated } = useAuth();

  // Load addresses from API when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadAddresses();
    } else {
      setAddresses([]);
    }
  }, [isAuthenticated]);

  const loadAddresses = React.useCallback(async () => {
    const result = await apiService.getAddresses();
    
    if (result.success && result.data) {
      // Handle both array and object with list property
      const data = result.data;
      const addressList = Array.isArray(data) ? data : Array.isArray(data.list) ? data.list : [];
      
      const mappedAddresses = addressList.map((addr) => ({
        id: addr.addressId || addr.id,
        name: addr.fullName || addr.name,
        phone: addr.phoneNumber || addr.phone,
        email: addr.email,
        houseFlatNo: addr.houseNumber || addr.houseFlatNo,
        address: addr.address,
        city: addr.city,
        pincode: addr.pincode,
        state: addr.state,
        type: (addr.addressType || addr.type || 'home').toLowerCase() as 'home' | 'office' | 'other',
        isDefault: addr.isDefault || false,
      }));
      
      setAddresses(mappedAddresses);
    }
  }, []);

  const addAddress = async (address: Omit<Address, 'id'>) => {
    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please login to add addresses",
        variant: "destructive",
      });
      return;
    }

    const payload = {
      fullName: address.name,
      phoneNumber: address.phone,
      email: address.email,
      houseNumber: address.houseFlatNo,
      address: address.address,
      city: address.city,
      pincode: address.pincode,
      state: address.state,
      addressType: address.type,
    };

    const result = await apiService.addAddress(payload);
    
    if (result.success) {
      await loadAddresses();
      toast({
        title: "Success",
        description: "Address added successfully",
      });
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to add address",
        variant: "destructive",
      });
    }
  };

  const updateAddress = async (id: string, address: Omit<Address, 'id'>) => {
    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please login to update addresses",
        variant: "destructive",
      });
      return;
    }

    const payload = {
      fullName: address.name,
      phoneNumber: address.phone,
      email: address.email,
      houseNumber: address.houseFlatNo,
      address: address.address,
      city: address.city,
      pincode: address.pincode,
      state: address.state,
      addressType: address.type,
    };

    const result = await apiService.updateAddress(id, payload);
    
    if (result.success) {
      await loadAddresses();
      toast({
        title: "Success",
        description: "Address updated successfully",
      });
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to update address",
        variant: "destructive",
      });
    }
  };

  const deleteAddress = async (id: string) => {
    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please login to delete addresses",
        variant: "destructive",
      });
      return;
    }

    const result = await apiService.removeAddress(id);
    
    if (result.success) {
      setAddresses(prev => prev.filter(addr => addr.id !== id));
      toast({
        title: "Success",
        description: "Address deleted successfully",
      });
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to delete address",
        variant: "destructive",
      });
    }
  };

  const setDefaultAddress = (id: string) => {
    setAddresses(prev =>
      prev.map(addr => ({
        ...addr,
        isDefault: addr.id === id
      }))
    );
  };

  const getDefaultAddress = () => {
    return addresses.find(addr => addr.isDefault) || addresses[0];
  };

  return (
    <AddressContext.Provider value={{
      addresses,
      addAddress,
      updateAddress,
      deleteAddress,
      setDefaultAddress,
      getDefaultAddress,
      loadAddresses
    }}>
      {children}
    </AddressContext.Provider>
  );
};

export const useAddress = () => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddress must be used within AddressProvider');
  }
  return context;
};
